import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  STATUS_FILTERS,
  clearFormDraft,
  fetchFindAll,
  fetchTimeQuota,
  getRequestStatusUi,
  itemId,
  padEmpId,
  parseSapDate,
  syncLeaveEncashment,
} from "./leaveEncashment.service";

type TabKey = "quota" | "compensation";

const LeaveEncashmentList: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;

  const [activeTab, setActiveTab] = useState<TabKey>("quota");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [quotaData, setQuotaData] = useState<any[]>([]);
  const [compensationData, setCompensationData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadQuota = useCallback(async () => {
    setQuotaData(await fetchTimeQuota(padEmpId()));
  }, []);

  const loadCompensation = useCallback(async (from = fromDate, to = toDate) => {
    setCompensationData(await fetchFindAll(from || undefined, to || undefined));
  }, [fromDate, toDate]);

  const loadActive = useCallback(
    async (tab: TabKey = activeTab, from = fromDate, to = toDate) => {
      setLoading(true);
      try {
        if (tab === "quota") await loadQuota();
        else await loadCompensation(from, to);
      } catch (err: any) {
        if (tab === "quota") setQuotaData([]);
        else setCompensationData([]);
        toast.error(Api.getErrorMessage(err) || err?.message || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    },
    [activeTab, fromDate, toDate, loadCompensation, loadQuota, t]
  );

  useEffect(() => {
    loadActive("quota");
    loadCompensation("", "").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredCompensation = useMemo(() => {
    if (!status) return compensationData;
    return compensationData.filter(
      (item) => String(item?.Status ?? "").toLowerCase() === status.toLowerCase()
    );
  }, [compensationData, status]);

  const handleSync = async () => {
    const empId = padEmpId();
    if (!empId) return;
    setSyncing(true);
    setLoading(true);
    try {
      const result = await syncLeaveEncashment(empId);
      if (!result.ok && result.message) toast.error(result.message);
      if (activeTab === "quota") await loadQuota();
      else await loadCompensation();
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || err?.message || t("something_went_wrong"));
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  };

  const openDetails = (item: any, type: TabKey) => {
    const ref = encodeURIComponent(itemId(item));
    navigate(`${routes.leaveEncashment.details}?type=${type}${ref ? `&ref=${ref}` : ""}`, {
      state: { item, type },
    });
  };

  const openCreate = () => {
    clearFormDraft();
    navigate(routes.leaveEncashment.create);
  };

  const currentData = activeTab === "quota" ? quotaData : filteredCompensation;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("leave_encashment_overview")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("leave_encashment_for_el_beyond_60_leave")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button type="button" className="btn btn-outline-secondary" onClick={handleSync} disabled={syncing || loading}>
              <i className={`ti ti-refresh me-1 ${syncing ? "le-spin" : ""}`}></i>
              {syncing ? t("syncing") : t("sync")}
            </button>
            <button type="button" className="btn btn-danger" onClick={openCreate}>
              <i className="ti ti-plus me-1"></i>
              {t("create_new_request")}
            </button>
          </div>
        </div>

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === "quota" ? "active" : ""}`} onClick={() => { setActiveTab("quota"); loadActive("quota"); }}>
              {t("leave_quota")}
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === "compensation" ? "active" : ""}`} onClick={() => { setActiveTab("compensation"); loadActive("compensation"); }}>
              {t("compensation")}
            </button>
          </li>
        </ul>

        {activeTab === "compensation" && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label fw-semibold small">{t("start_date")}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    max={toDate || new Date().toISOString().slice(0, 10)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFromDate(value);
                      loadActive("compensation", value, toDate);
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold small">{t("end_date")}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    min={fromDate || undefined}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (fromDate && value && value < fromDate) {
                        toast.error(t("end_date_error"));
                        return;
                      }
                      setToDate(value);
                      loadActive("compensation", fromDate, value);
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold small">{t("Filter")}</label>
                  <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_FILTERS.map((opt) => (
                      <option key={opt.id || "all"} value={opt.id}>{t(opt.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : currentData.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">{t("no_records_found")}</div>
          </div>
        ) : (
          <div className="row g-3">
            {currentData.map((item, index) => {
              const statusUi = getRequestStatusUi(item.RequestStatus, t);
              return (
                <div className="col-xl-6" key={itemId(item) || `${item.QuotaType || "row"}-${index}`}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center gap-2">
                      <span className="fw-bold">{t("leave_type")}: {item.QuotaText || "-"}</span>
                      {activeTab === "compensation" && <span className={statusUi.badge}>{statusUi.label}</span>}
                    </div>
                    <div className="card-body">
                      {activeTab === "quota" ? (
                        <>
                          <InfoRow label={t("deductible_from")} value={parseSapDate(item.DeductionFrom)} />
                          <InfoRow label={t("deductible_to")} value={parseSapDate(item.DeductionTo)} />
                          <InfoRow label={t("entitlement")} value={item.QuotaNumber} />
                          <InfoRow label={t("remainder")} value={item.RemQuota} />
                        </>
                      ) : (
                        <>
                          <InfoRow label={t("leave_encashment_method")} value={item.Description} />
                          <InfoRow label={t("encashed_days")} value={item.QuotaNumber} />
                          <InfoRow label={t("off_cycle_status")} value={item.OffCycleIndicator || "-"} />
                          <InfoRow label={t("remarks")} value={item.Comment} />
                        </>
                      )}
                      <button type="button" className="btn btn-danger w-100 mt-3" onClick={() => openDetails(item, activeTab)}>
                        {t("view_details")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .le-spin { animation: le-spin 1s linear infinite; }
        @keyframes le-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default LeaveEncashmentList;
